// Modal "Alterar valor de todos os bairros" (bulk update).
// Modo "definir": todos os bairros ficam com o mesmo valor.
// Modo "ajustar": soma/subtrai R$X do valor atual de cada bairro — só aparece
// com CEP Único desativado (pedido do dono). Piso 0 garantido no backend.
// Os dois valores são digitados de caixa registradora (dono, 26/10/2026):
// começa em 0,00 e cada dígito entra pela direita (1 → 0,01; 790 → 7,90).

import { DollarSign, Minus, Plus } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { InputDecimal } from "../../../components/ui/input-decimal";
import { Label } from "../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

export default function ValorMassaDialog({
  open,
  onOpenChange,
  valor,
  setValor,
  modo,
  setModo,
  operacao,
  setOperacao,
  saving,
  onConfirmar,
  totalBairros,
  cepUnico,
}) {
  const mostrarAjuste = !cepUnico;
  const ajustando = mostrarAjuste && modo === "ajustar";
  const valorNum = parseFloat(String(valor).replace(",", ".")) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            Alterar Valor de Todos os Bairros
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {mostrarAjuste && (
            <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted">
              <button
                type="button"
                onClick={() => setModo("definir")}
                className={`text-sm font-medium rounded-md py-1.5 transition-colors ${
                  modo === "definir"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Definir valor
              </button>
              <button
                type="button"
                onClick={() => setModo("ajustar")}
                className={`text-sm font-medium rounded-md py-1.5 transition-colors ${
                  modo === "ajustar"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Somar / Subtrair
              </button>
            </div>
          )}

          {ajustando ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setOperacao("somar")}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm font-semibold transition-colors ${
                    operacao === "somar"
                      ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <Plus className="w-4 h-4" /> Somar
                </button>
                <button
                  type="button"
                  onClick={() => setOperacao("subtrair")}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-sm font-semibold transition-colors ${
                    operacao === "subtrair"
                      ? "border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <Minus className="w-4 h-4" /> Subtrair
                </button>
              </div>

              <div>
                <Label>Valor do ajuste (R$)</Label>
                <InputDecimal
                  data-e2e="bairros-valor-ajuste"
                  decimals={2}
                  value={valor}
                  onChange={setValor}
                  className="mt-1 text-left"
                />
              </div>

              <p className="text-sm text-muted-foreground">
                {valorNum > 0 ? (
                  <>
                    <strong>R$ {valorNum.toFixed(2).replace(".", ",")}</strong> será{" "}
                    {operacao === "somar" ? "somado ao" : "subtraído do"} valor de entrega
                    atual de <strong>{totalBairros}</strong> bairro(s).
                    {operacao === "subtrair" && " Nenhum valor fica abaixo de R$ 0,00."}
                  </>
                ) : (
                  "Informe quanto somar ou subtrair do valor atual de cada bairro."
                )}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                O valor informado será aplicado a todos os {totalBairros} bairros
                cadastrados.
              </p>

              <div>
                <Label>Novo Valor de Entrega (R$)</Label>
                <InputDecimal
                  data-e2e="bairros-valor-todos"
                  decimals={2}
                  value={valor}
                  onChange={setValor}
                  className="mt-1 text-left"
                />
              </div>
            </>
          )}

          <div className="flex gap-2 pt-4">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button className="flex-1" onClick={onConfirmar} disabled={saving}>
              {saving ? "Aplicando..." : ajustando ? "Aplicar Ajuste" : "Aplicar a Todos"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
